import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { warehouseStockApi } from "../../../api/inventory/warehouse-stock";
import { ResourceLoading } from "../../utils/ResourceLoading";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Chip } from "@mui/material";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";

const formatNumber = (num) => {
  if (num === null || num === undefined) return "0,00";
  return parseFloat(num).toLocaleString("nb-NO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("nb-NO");
};

const getTypeColor = (type) => {
  switch (type) {
    case "inventory_count":
      return "primary";
    case "inventory_correction":
      return "warning";
    case "order":
    case "report":
    case "work_order":
      return "error";
    case "goods_receipt":
      return "success";
    default:
      return "default";
  }
};

const getTypeIcon = (type, quantity) => {
  if (quantity > 0) {
    return <ArrowUpIcon className="h-4 w-4 text-green-600" />;
  } else {
    return <ArrowDownIcon className="h-4 w-4 text-red-600" />;
  }
};

const getTransactionPath = (type, id) => {
  switch (type) {
    case "inventory_count":
      return `/dashboard/inventory/counts/${id}`;
    case "inventory_correction":
      return `/dashboard/inventory/corrections/${id}`;
    case "order":
      return `/dashboard/orders/${id}`;
    case "report":
      return `/dashboard/service/reports/${id}`;
    case "work_order":
      return `/dashboard/work_orders/${id}`;
    case "goods_receipt":
      return `/dashboard/inventory/goods-receipts/${id}`;
    default:
      return null;
  }
};

export const ProductStockReport = ({ product }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (product?.id) {
      fetchStockReport();
    }
  }, [product?.id]);

  const fetchStockReport = async () => {
    setIsLoading(true);
    try {
      const response = await warehouseStockApi.getProductStockReport(product.id);
      if (!response.error && response.data) {
        setTransactions(response.data);
      }
    } catch (error) {
      console.error("Error fetching product stock report:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <ResourceLoading />
        </div>
      </div>
    );
  }

  return (
    <div className="shadow sm:overflow-hidden sm:rounded-md">
      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t("product.stock_report.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("product.stock_report.description")}
          </p>
        </div>

        {transactions && transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("product.stock_report.date")}</TableCell>
                  <TableCell>{t("product.stock_report.type")}</TableCell>
                  <TableCell>{t("product.stock_report.transaction_title")}</TableCell>
                  <TableCell align="right">
                    {t("product.stock_report.quantity")}
                  </TableCell>
                  <TableCell>{t("product.stock_report.storage")}</TableCell>
                  <TableCell>{t("product.stock_report.status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow key={`${transaction.type}-${transaction.id}-${index}`} hover>
                    <TableCell>{formatDate(transaction.date)}</TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.type_label}
                        color={getTypeColor(transaction.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTypeIcon(transaction.type, transaction.quantity)}
                        {getTransactionPath(transaction.type, transaction.id) ? (
                          <a
                            href={getTransactionPath(transaction.type, transaction.id)}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(getTransactionPath(transaction.type, transaction.id));
                            }}
                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {transaction.title || `#${transaction.id}`}
                          </a>
                        ) : (
                          <span className="font-medium">
                            {transaction.title || `#${transaction.id}`}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell align="right">
                      <span
                        className={
                          transaction.quantity > 0
                            ? "text-green-600 font-semibold"
                            : transaction.quantity < 0
                            ? "text-red-600 font-semibold"
                            : ""
                        }
                      >
                        {transaction.quantity > 0 ? "+" : ""}
                        {formatNumber(transaction.quantity)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {transaction.storage
                        ? transaction.storage.title ||
                          transaction.storage.storage_number ||
                          `Lager ${transaction.storage.id}`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {transaction.approved ? (
                        <Chip
                          label={t("product.stock_report.approved")}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label={t("product.stock_report.pending")}
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12">
            <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("product.stock_report.no_transactions")}
            </h3>
            <p className="text-gray-500">
              {t("product.stock_report.no_transactions_description")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

